import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import './Admin.css';

interface User {
  userId: string;
  email: string;
  registrationDate: number;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvalStatusUpdatedAt?: number;
  rejectionReason?: string;
  totalJobsSubmitted: number;
  activeJobCount: number;
}

interface UsageMetrics {
  metricId: string;
  transcribeMinutesUsed: number;
  transcribeMinutesLimit: number;
  translateCharsUsed: number;
  translateCharsLimit: number;
  s3StorageGB: number;
  s3StorageLimit: number;
  lambdaInvocations: number;
  lambdaInvocationsLimit: number;
  lastUpdated: number;
  dailyUsage?: {
    [date: string]: {
      transcribeMinutes: number;
      translateChars: number;
      lambdaInvocations: number;
    };
  };
}

export const Admin: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectionReasons, setRejectionReasons] = useState<{ [userId: string]: string }>({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParam = filter === 'all' ? undefined : filter;
      const [usersData, usageData] = await Promise.all([
        apiClient.getAdminUsers(filterParam),
        apiClient.getAdminUsage(),
      ]);

      setUsers(usersData);
      setUsage(usageData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    try {
      await apiClient.updateUserApproval(userId, 'approved');
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleReject = async (userId: string) => {
    const reason = rejectionReasons[userId];
    if (!reason || reason.trim() === '') {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      await apiClient.updateUserApproval(userId, 'rejected', reason);
      setRejectionReasons({ ...rejectionReasons, [userId]: '' });
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject user');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatPercentage = (used: number, limit: number) => {
    return ((used / limit) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="admin-container">
        <h1>Admin Dashboard</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <h1>Admin Dashboard</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h1>Admin Dashboard</h1>

      {/* Usage Metrics Section */}
      {usage && (
        <Card className="usage-section">
          <h2>Free Tier Usage - {usage.metricId}</h2>
          <div className="metrics-grid">
            <div className="metric">
              <h3>Transcription</h3>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${formatPercentage(usage.transcribeMinutesUsed, usage.transcribeMinutesLimit)}%` }}
                />
              </div>
              <p>{usage.transcribeMinutesUsed.toFixed(1)} / {usage.transcribeMinutesLimit} minutes ({formatPercentage(usage.transcribeMinutesUsed, usage.transcribeMinutesLimit)}%)</p>
            </div>

            <div className="metric">
              <h3>Translation</h3>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${formatPercentage(usage.translateCharsUsed, usage.translateCharsLimit)}%` }}
                />
              </div>
              <p>{usage.translateCharsUsed.toLocaleString()} / {usage.translateCharsLimit.toLocaleString()} chars ({formatPercentage(usage.translateCharsUsed, usage.translateCharsLimit)}%)</p>
            </div>

            <div className="metric">
              <h3>S3 Storage</h3>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${formatPercentage(usage.s3StorageGB, usage.s3StorageLimit)}%` }}
                />
              </div>
              <p>{usage.s3StorageGB.toFixed(2)} / {usage.s3StorageLimit} GB ({formatPercentage(usage.s3StorageGB, usage.s3StorageLimit)}%)</p>
            </div>

            <div className="metric">
              <h3>Lambda Invocations</h3>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${formatPercentage(usage.lambdaInvocations, usage.lambdaInvocationsLimit)}%` }}
                />
              </div>
              <p>{usage.lambdaInvocations.toLocaleString()} / {usage.lambdaInvocationsLimit.toLocaleString()} ({formatPercentage(usage.lambdaInvocations, usage.lambdaInvocationsLimit)}%)</p>
            </div>
          </div>
          <p className="last-updated">Last updated: {formatDate(usage.lastUpdated)}</p>
        </Card>
      )}

      {/* User Management Section */}
      <Card className="users-section">
        <div className="section-header">
          <h2>User Management</h2>
          <div className="filter-buttons">
            <Button
              variant={filter === 'pending' ? 'primary' : 'secondary'}
              onClick={() => setFilter('pending')}
            >
              Pending
            </Button>
            <Button
              variant={filter === 'approved' ? 'primary' : 'secondary'}
              onClick={() => setFilter('approved')}
            >
              Approved
            </Button>
            <Button
              variant={filter === 'rejected' ? 'primary' : 'secondary'}
              onClick={() => setFilter('rejected')}
            >
              Rejected
            </Button>
            <Button
              variant={filter === 'all' ? 'primary' : 'secondary'}
              onClick={() => setFilter('all')}
            >
              All
            </Button>
          </div>
        </div>

        {users.length === 0 ? (
          <p>No users found</p>
        ) : (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Registration Date</th>
                  <th>Status</th>
                  <th>Jobs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.userId}>
                    <td>{user.email}</td>
                    <td>{formatDate(user.registrationDate)}</td>
                    <td>
                      <span className={`status-badge status-${user.approvalStatus || 'pending'}`}>
                        {user.approvalStatus || 'pending'}
                      </span>
                    </td>
                    <td>{user.totalJobsSubmitted}</td>
                    <td>
                      {(user.approvalStatus === 'pending' || !user.approvalStatus) && (
                        <div className="action-buttons">
                          <Button
                            variant="primary"
                            size="small"
                            onClick={() => handleApprove(user.userId)}
                          >
                            Approve
                          </Button>
                          <div className="reject-section">
                            <input
                              type="text"
                              placeholder="Rejection reason"
                              value={rejectionReasons[user.userId] || ''}
                              onChange={(e) =>
                                setRejectionReasons({
                                  ...rejectionReasons,
                                  [user.userId]: e.target.value,
                                })
                              }
                              className="rejection-input"
                            />
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleReject(user.userId)}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}
                      {user.approvalStatus === 'approved' && (
                        <span className="approved-text">Approved</span>
                      )}
                      {user.approvalStatus === 'rejected' && (
                        <div className="rejected-info">
                          <span>Rejected</span>
                          {user.rejectionReason && (
                            <p className="rejection-reason">Reason: {user.rejectionReason}</p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
